import React from 'react'

import type { CompanyOption } from '../../types/catalog'
import type { PricingRegion, PricingTerm } from '../../types/deal'
import { Combobox } from '../Combobox'
import { RecurringCard } from './RecurringCard'
import type {
  BillingTypeOption,
  DealDraft,
  DealDraftValidation,
  TouchedFields,
} from './types'

interface GeneralTabProps {
  draft: DealDraft
  patch: (next: Partial<DealDraft>) => void
  isNew: boolean
  companyOptions: CompanyOption[]
  validation: DealDraftValidation
  touched: TouchedFields
  markTouched: (field: keyof DealDraft) => void
}

function businessOptionsForCompany(
  companyOptions: CompanyOption[],
  companyId: string,
): { value: string; label: string }[] {
  const match = companyOptions.find((entry) => entry.id === companyId)
  return match ? match.businesses.map((business) => ({ value: business.id, label: business.name })) : []
}

export const GeneralTab: React.FunctionComponent<GeneralTabProps> = (props) => {
  const { draft, patch, isNew, companyOptions, validation, touched, markTouched } = props
  const companyComboOptions = companyOptions.map((entry) => ({ value: entry.id, label: entry.name }))
  const businessOptions = businessOptionsForCompany(companyOptions, draft.companyId)
  const businessDisabled = businessOptions.length === 0
  const errors = validation.errors
  const showError = (field: keyof DealDraft): string | undefined =>
    touched[field] ? errors[field] : undefined

  return (
    <>
      <div className="section-header" style={{ marginTop: 16 }}>
        Customer
      </div>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">
            Company <span className="req">*</span>
          </label>
          <Combobox
            value={draft.companyId}
            onChange={(nextCompanyId) => {
              const stillValid = businessOptionsForCompany(companyOptions, nextCompanyId).some(
                (option) => option.value === draft.businessId,
              )
              patch({ companyId: nextCompanyId, businessId: stillValid ? draft.businessId : '' })
              markTouched('companyId')
            }}
            options={companyComboOptions}
            required
            aria-label="Company"
          />
          {showError('companyId') && <div className="form-error">{showError('companyId')}</div>}
        </div>
        <div className="form-group">
          <label className="form-label">
            Business <span className="req">*</span>
          </label>
          <Combobox
            value={draft.businessId}
            onChange={(nextBusinessId) => {
              patch({ businessId: nextBusinessId })
              markTouched('businessId')
            }}
            options={businessOptions}
            disabled={businessDisabled}
            placeholder={businessDisabled ? 'Select a company first' : undefined}
            aria-label="Business"
          />
          {showError('businessId') && <div className="form-error">{showError('businessId')}</div>}
        </div>
        <div className="form-group">
          <label className="form-label">
            Customer Name <span className="req">*</span>
          </label>
          <input
            className={`form-input${showError('customerName') ? ' has-error' : ''}`}
            placeholder="Customer contact name"
            value={draft.customerName}
            onChange={(event) => patch({ customerName: event.target.value })}
            onBlur={() => markTouched('customerName')}
          />
          {showError('customerName') && (
            <div className="form-error">{showError('customerName')}</div>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">
            Email <span className="req">*</span>
          </label>
          <input
            className={`form-input${showError('customerEmail') ? ' has-error' : ''}`}
            type="email"
            placeholder="customer@company.com"
            value={draft.customerEmail}
            onChange={(event) => patch({ customerEmail: event.target.value })}
            onBlur={() => markTouched('customerEmail')}
          />
          {showError('customerEmail') && (
            <div className="form-error">{showError('customerEmail')}</div>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Telephone</label>
          <input
            className="form-input"
            placeholder="Contact phone number"
            value={draft.customerPhone}
            onChange={(event) => patch({ customerPhone: event.target.value })}
          />
        </div>
        <div className="form-group">
          <label className="form-label">EIN / Tax ID</label>
          <input
            className="form-input"
            placeholder="Tax identification number"
            value={draft.customerEIN}
            onChange={(event) => patch({ customerEIN: event.target.value })}
          />
        </div>
        <div className="form-group full">
          <label className="form-label">
            Shipping Address <span className="req">*</span>
          </label>
          <textarea
            className={`form-textarea${showError('shippingAddress') ? ' has-error' : ''}`}
            rows={2}
            placeholder="Street address, city, postal code"
            style={{ resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
            value={draft.shippingAddress}
            onChange={(event) => patch({ shippingAddress: event.target.value })}
            onBlur={() => markTouched('shippingAddress')}
          />
          {showError('shippingAddress') && (
            <div className="form-error">{showError('shippingAddress')}</div>
          )}
        </div>
      </div>

      <div className="section-header">Subscription</div>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Activation Date</label>
          <input
            className="form-input"
            type="date"
            disabled={isNew}
            placeholder={isNew ? 'Auto-set when shipments arrive' : ''}
            value={draft.activationISO}
            onChange={(event) => patch({ activationISO: event.target.value })}
          />
          <div className="form-hint">
            {isNew ? 'Auto-set when shipments arrive.' : 'Auto-computed. Edit to override.'}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Expiration Date</label>
          <input
            className={`form-input${showError('expirationISO') ? ' has-error' : ''}`}
            type="date"
            disabled={isNew}
            value={draft.expirationISO}
            onChange={(event) => patch({ expirationISO: event.target.value })}
            onBlur={() => markTouched('expirationISO')}
          />
          {showError('expirationISO') ? (
            <div className="form-error">{showError('expirationISO')}</div>
          ) : (
            <div className="form-hint">Auto from activation + term.</div>
          )}
        </div>
      </div>

      <div className="section-header">Pricing</div>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Region</label>
          <select
            className="form-input"
            value={String(draft.pricingRegion)}
            onChange={(event) =>
              patch({ pricingRegion: Number(event.target.value) as PricingRegion })
            }
          >
            <option value="1">Region 1 — US, Canada, Australia, UK, Western Europe</option>
            <option value="2">Region 2 — Japan, South Korea, UAE, Saudi Arabia, Israel</option>
          </select>
          <div className="form-hint">Y-Series pricing differs by region.</div>
        </div>
        <div className="form-group">
          <label className="form-label">Commitment Term</label>
          <select
            className="form-input"
            value={String(draft.pricingTerm)}
            onChange={(event) =>
              patch({ pricingTerm: Number(event.target.value) as PricingTerm })
            }
          >
            <option value="1">1 year — list price</option>
            <option value="3">3 years — AI Eye −7%</option>
            <option value="5">5 years — AI Eye −12%</option>
          </select>
          <div className="form-hint">Multi-year discount applies to AI Eye Camera Platform.</div>
        </div>
      </div>

      <div className="section-header">Billing</div>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">
            Quote No. <span className="req">*</span>
          </label>
          <input
            className={`form-input${showError('quoteNo') ? ' has-error' : ''}`}
            value={draft.quoteNo}
            onChange={(event) => patch({ quoteNo: event.target.value })}
            onBlur={() => markTouched('quoteNo')}
          />
          {showError('quoteNo') && <div className="form-error">{showError('quoteNo')}</div>}
        </div>
        <div className="form-group">
          <label className="form-label">Quote URL</label>
          <div className="input-with-link">
            <input
              className="form-input"
              value={draft.quoteUrl}
              onChange={(event) => patch({ quoteUrl: event.target.value })}
            />
            <a
              href={draft.quoteUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="link-icon"
              title="Open quote"
            >
              ↗
            </a>
          </div>
        </div>
      </div>
      <div className="form-group" style={{ marginBottom: 16 }}>
        <label className="form-label">
          Billing Type <span className="req">*</span>
        </label>
        <div style={{ display: 'flex', gap: 0 }}>
          <input
            type="number"
            min={1}
            value={draft.billingCount}
            onChange={(event) =>
              patch({ billingCount: Math.max(1, Number(event.target.value) || 1) })
            }
            disabled={draft.billingType === 'onetime'}
            style={{
              borderRadius: '8px 0 0 8px',
              borderRight: 'none',
              flex: 1,
              height: 40,
              padding: '0 12px',
              border: '1px solid #e5e5ea',
              fontSize: 14,
              fontFamily: 'inherit',
            }}
          />
          <select
            value={draft.billingType}
            onChange={(event) => patch({ billingType: event.target.value as BillingTypeOption })}
            style={{
              borderRadius: '0 8px 8px 0',
              width: 120,
              height: 40,
              padding: '0 12px',
              border: '1px solid #e5e5ea',
              fontSize: 14,
              background: 'white',
              color: '#1d1d1f',
              fontFamily: 'inherit',
            }}
          >
            <option value="annual">Year</option>
            <option value="quarterly">Quarter</option>
            <option value="monthly">Month</option>
            <option value="onetime">One-time</option>
          </select>
        </div>
      </div>

      {draft.billingType !== 'onetime' && <RecurringCard draft={draft} patch={patch} />}

      <div className="section-header">Notes</div>
      <div className="form-grid">
        <div className="form-group full">
          <label className="form-label">Memo</label>
          <textarea
            className="form-textarea"
            rows={2}
            placeholder="Internal notes..."
            value={draft.memo}
            onChange={(event) => patch({ memo: event.target.value })}
            style={{ resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
          />
        </div>
      </div>
    </>
  )
}
